'use client'

import { useAuth } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'
import { startTransition, useEffect, useRef, useState } from 'react'
import {
  readPublicComposerDraft,
  readPublicComposerDraftFiles,
  toPromptInputAttachmentInputs,
} from '@/components/chat/public-composer-draft'
import { LaunchComposer } from '@/components/chat-launch/launch-composer'
import { LaunchComposerSkeleton } from '@/components/chat-launch/launch-composer-skeleton'

/** Shared by the live composer and its skeleton so the swap cannot shift. */
const HOMEPAGE_COMPOSER_CLASSNAME = 'relative w-full'

export function HomepagePromptCollector() {
  const router = useRouter()
  const { isLoaded, isSignedIn } = useAuth()
  const [isStorageReady, setIsStorageReady] = useState(false)
  const [restoredDraft, setRestoredDraft] =
    useState<ReturnType<typeof readPublicComposerDraft>>(null)
  const [restoredAttachments, setRestoredAttachments] = useState<
    ReturnType<typeof toPromptInputAttachmentInputs>
  >([])
  const hasRedirectedPending = useRef(false)

  useEffect(() => {
    let isCancelled = false

    const restoreDraft = async () => {
      const draft = readPublicComposerDraft()
      const files = await readPublicComposerDraftFiles(draft?.filesKey)
      if (isCancelled) {
        return
      }

      setRestoredDraft(draft)
      setRestoredAttachments(toPromptInputAttachmentInputs(files))
      setIsStorageReady(true)
    }

    void restoreDraft()

    return () => {
      isCancelled = true
    }
  }, [])

  useEffect(() => {
    if (
      !isStorageReady ||
      !isLoaded ||
      !isSignedIn ||
      hasRedirectedPending.current
    ) {
      return
    }

    hasRedirectedPending.current = true
    startTransition(() => {
      router.push('/home')
    })
  }, [isLoaded, isSignedIn, isStorageReady, router])

  if (!isStorageReady || isSignedIn) {
    return <LaunchComposerSkeleton className={HOMEPAGE_COMPOSER_CLASSNAME} />
  }

  return (
    <LaunchComposer
      mode="public"
      className={HOMEPAGE_COMPOSER_CLASSNAME}
      initialInput={restoredDraft?.text ?? ''}
      initialModelId={restoredDraft?.modelId}
      initialAttachments={restoredAttachments}
      initialPublicChatId={restoredDraft?.chatId}
    />
  )
}
